import { Link, usePathname } from 'expo-router';
import { ChevronLeft, ChevronRight } from 'lucide-react-native';
import { memo, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Easing,
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import {
  type NavItem as NavItemType,
  type NavSection,
  navSections,
  resolveChromeActiveNavHref,
} from '../../config/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { useTabsNavigation } from '../../contexts/TabsNavigationContext';
import { useTranslation } from '../../contexts/TranslationContext';
import { useNavigateToTab } from '../../hooks/useNavigateToTab';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colors } from '../../theme/colors';
import { tokens } from '../../theme/tokens';
import { darkPrecisionFontFamilies } from '../../theme/typography';
import {
  type CommandDeckIcon,
  LogoutIcon,
  SettingsIcon,
  SparkleBurstIcon,
} from '../icons/commandDeckIcons';
import { Avatar } from '../primitives/Avatar';
import { Box } from '../primitives/Box';
import { ScrollEdgeFade, useScrollEdges } from '../primitives/ScrollEdgeFade';
import { HStack, VStack } from '../primitives/Stack';
import { Text } from '../primitives/Text';

/**
 * Modern sidebar iteration (FLOSEAI design refresh). Same props + nav wiring as
 * `Sidebar`, restyled toward a cleaner dark UI. The collapse is smooth: icons
 * stay put while every label fades out via a single `reveal` value, synced with
 * the container's width spring (driven by DashboardLayout) — no label "pop".
 */

const NOOP = () => undefined;
const AUTOMATION_SECTION_KEY = 'layout.navSections.automation';
const isWeb = Platform.OS === 'web';

interface SidebarProps {
  readonly expanded?: boolean;
  readonly onToggle?: () => void;
  readonly onNavigate?: () => void;
}

function Tooltip({
  label,
  visible,
  children,
}: Readonly<{ label: string; visible: boolean; children: ReactNode }>) {
  const [hovered, setHovered] = useState(false);
  if (!isWeb || !visible) return <>{children}</>;
  return (
    <View
      style={styles.tooltipAnchor}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {children}
      {hovered && (
        <View style={styles.tooltip}>
          <Text size="xs" weight="medium" color={colors.text.primary}>
            {label}
          </Text>
        </View>
      )}
    </View>
  );
}

// A label that fades with the collapse and is non-interactive when hidden.
function FadeLabel({
  reveal,
  expanded,
  children,
}: Readonly<{ reveal: Animated.Value; expanded: boolean; children: ReactNode }>) {
  return (
    <Animated.View
      pointerEvents={expanded ? 'auto' : 'none'}
      style={[styles.fadeLabel, { opacity: reveal }]}
    >
      {children}
    </Animated.View>
  );
}

function BrandHeader({
  expanded,
  reveal,
}: Readonly<{ expanded: boolean; reveal: Animated.Value }>) {
  const { t } = useTranslation();
  const navigateToTab = useNavigateToTab();
  return (
    // Same navRow + navIcon structure as every nav item, so the logo sits on
    // the exact same icon axis (x = 28) instead of a coincidental match.
    <Pressable onPress={() => navigateToTab('/dashboard')} style={styles.navRow}>
      <View style={styles.navIcon}>
        <Image
          source={require('../../../assets/icon.png')}
          style={styles.brandLogo}
          resizeMode="contain"
          accessibilityLabel={t('layout.sidebar.brandName')}
        />
      </View>
      <FadeLabel reveal={reveal} expanded={expanded}>
        <View style={styles.brandWordmarkRow} accessibilityLabel={t('layout.sidebar.brandName')}>
          <Text style={styles.brandWordmark} numberOfLines={1}>
            Flow
          </Text>
          <Text style={[styles.brandWordmark, styles.brandWordmarkAccent]} numberOfLines={1}>
            SellAI
          </Text>
        </View>
        <Text variant="caption" size="xs" color={colors.text.tertiary} numberOfLines={1}>
          {t('layout.sidebar.brandSubtitle')}
        </Text>
      </FadeLabel>
    </Pressable>
  );
}

const NavRow = memo(function NavRow({
  item,
  expanded,
  active,
  accent,
  reveal,
  onNavigate,
}: Readonly<{
  item: NavItemType;
  expanded: boolean;
  active: boolean;
  accent: boolean;
  reveal: Animated.Value;
  onNavigate?: () => void;
}>) {
  const { t } = useTranslation();
  const navigateToTab = useNavigateToTab();
  const [hovered, setHovered] = useState(false);
  const label = t(item.labelKey);
  const navKey = item.href.replace(/^\//, '');

  const onEnter = isWeb ? () => setHovered(true) : undefined;
  const onLeave = isWeb ? () => setHovered(false) : undefined;
  const handlePress = useCallback(() => {
    navigateToTab(item.href);
    onNavigate?.();
  }, [navigateToTab, item.href, onNavigate]);

  const iconColor = active
    ? colors.primary[400]
    : hovered
      ? colors.text.secondary
      : colors.text.tertiary;

  const row = (
    <View onPointerEnter={onEnter} onPointerLeave={onLeave}>
      <Pressable
        testID={`nav_${navKey}_link`}
        accessibilityRole="link"
        accessibilityLabel={label}
        accessibilityState={{ selected: active }}
        onPress={handlePress}
        style={[
          styles.navRow,
          hovered && !active && styles.navRowHover,
          active && styles.navRowActive,
        ]}
      >
        <View style={styles.navIcon}>
          <item.icon color={iconColor} size={18} strokeWidth={active ? 2.2 : 1.8} />
        </View>
        <FadeLabel reveal={reveal} expanded={expanded}>
          <View style={styles.navLabelRow}>
            <Text
              variant="body"
              size="sm"
              weight={active ? 'semibold' : 'medium'}
              color={active ? colors.text.primary : colors.text.secondary}
              numberOfLines={1}
              style={styles.navLabelText}
            >
              {label}
            </Text>
            {accent && <SparkleBurstIcon size={14} color={colors.primary.neon} strokeWidth={2} />}
          </View>
        </FadeLabel>
      </Pressable>
    </View>
  );

  return expanded ? (
    row
  ) : (
    <Tooltip label={label} visible>
      {row}
    </Tooltip>
  );
});

NavRow.displayName = 'NavRow';

const Section = memo(function Section({
  section,
  expanded,
  activeHref,
  reveal,
  onNavigate,
}: Readonly<{
  section: NavSection;
  expanded: boolean;
  activeHref: string | null;
  reveal: Animated.Value;
  onNavigate?: () => void;
}>) {
  const { t } = useTranslation();
  const accent = section.titleKey === AUTOMATION_SECTION_KEY;
  return (
    <VStack spacing="xs">
      <FadeLabel reveal={reveal} expanded={expanded}>
        <Text
          variant="caption"
          size="xs"
          color={colors.text.tertiary}
          weight="semibold"
          uppercase
          style={styles.sectionTitle}
        >
          {t(section.titleKey)}
        </Text>
      </FadeLabel>
      {section.items.map((item) => (
        <NavRow
          key={item.href}
          item={item}
          expanded={expanded}
          active={item.href === activeHref}
          accent={accent}
          reveal={reveal}
          onNavigate={onNavigate}
        />
      ))}
    </VStack>
  );
});

Section.displayName = 'Section';

function FooterAction({
  icon: Icon,
  label,
  expanded,
  reveal,
  onPress,
  danger,
  testID,
}: Readonly<{
  icon: CommandDeckIcon;
  label: string;
  expanded: boolean;
  reveal: Animated.Value;
  onPress?: () => void;
  danger?: boolean;
  testID?: string;
}>) {
  const [hovered, setHovered] = useState(false);
  const onEnter = isWeb ? () => setHovered(true) : undefined;
  const onLeave = isWeb ? () => setHovered(false) : undefined;
  const tint = danger ? colors.status.error.light : colors.text.tertiary;
  const hoverTint = danger ? colors.status.error.light : colors.text.secondary;

  const row = (
    <View onPointerEnter={onEnter} onPointerLeave={onLeave}>
      <Pressable
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={label}
        onPress={onPress}
        style={[styles.navRow, hovered && styles.navRowHover]}
      >
        <View style={styles.navIcon}>
          <Icon color={hovered ? hoverTint : tint} size={18} strokeWidth={1.8} />
        </View>
        <FadeLabel reveal={reveal} expanded={expanded}>
          <Text
            variant="body"
            size="sm"
            color={danger ? colors.status.error.light : colors.text.secondary}
            numberOfLines={1}
          >
            {label}
          </Text>
        </FadeLabel>
      </Pressable>
    </View>
  );

  return expanded ? (
    row
  ) : (
    <Tooltip label={label} visible>
      {row}
    </Tooltip>
  );
}

function ProfileCard({
  expanded,
  reveal,
}: Readonly<{ expanded: boolean; reveal: Animated.Value }>) {
  const { backendUser, auth0User } = useAuth();
  const { t } = useTranslation();
  const displayName =
    backendUser?.fullName || auth0User?.name || auth0User?.email || t('common.fallbacks.user');
  const role = backendUser?.role
    ? backendUser.role.replaceAll('_', ' ').replaceAll(/\b\w/g, (c) => c.toUpperCase())
    : t('common.fallbacks.member');
  const avatarSrc = auth0User?.picture;

  const inner = (
    <HStack spacing="sm" align="center" style={styles.profileRow}>
      <View style={styles.navIcon}>
        <Avatar size="sm" src={avatarSrc} name={displayName} />
      </View>
      <FadeLabel reveal={reveal} expanded={expanded}>
        <Text
          variant="body"
          size="sm"
          color={colors.text.primary}
          weight="semibold"
          numberOfLines={1}
        >
          {displayName}
        </Text>
        <Text variant="caption" size="xs" color={colors.text.tertiary} numberOfLines={1}>
          {role}
        </Text>
      </FadeLabel>
    </HStack>
  );

  const link = (
    <Link href="/settings" asChild>
      <Pressable
        testID="nav_user_profile_link"
        style={expanded ? styles.profileCard : styles.navRow}
      >
        {inner}
      </Pressable>
    </Link>
  );

  return expanded ? (
    link
  ) : (
    <Tooltip label={displayName} visible>
      {link}
    </Tooltip>
  );
}

function SidebarModernImpl({ expanded = true, onToggle = NOOP, onNavigate }: SidebarProps) {
  const pathname = usePathname() || '/';
  const { logout } = useAuth();
  const { t } = useTranslation();
  const navigateToTab = useNavigateToTab();
  const reducedMotion = useReducedMotion();
  const tabsNav = useTabsNavigation();
  const activeHref = useMemo(
    () => resolveChromeActiveNavHref(tabsNav?.activeTabRouteName ?? null, pathname),
    [tabsNav?.activeTabRouteName, pathname],
  );

  const reveal = useRef(new Animated.Value(expanded ? 1 : 0)).current;
  useEffect(() => {
    Animated.timing(reveal, {
      toValue: expanded ? 1 : 0,
      duration: reducedMotion ? 0 : 220,
      easing: Easing.inOut(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [expanded, reveal, reducedMotion]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }, [logout]);

  const handleSettings = useCallback(() => {
    navigateToTab('/settings');
    onNavigate?.();
  }, [navigateToTab, onNavigate]);

  const ToggleIcon = expanded ? ChevronLeft : ChevronRight;

  // The nav list scrolls once the window is short, but with the indicator off
  // there was nothing to say so: at a 700px viewport "Voice Agents" sat 76px
  // below the fold and the deck simply looked like it had no such destination.
  const { atStart: navAtStart, atEnd: navAtEnd, scrollProps: navScrollProps } = useScrollEdges();

  return (
    <View style={styles.sidebar} {...(isWeb ? ({ role: 'navigation' } as object) : {})}>
      <VStack spacing="none" fill style={styles.top}>
        <Box paddingX="sm" paddingY="md">
          <BrandHeader expanded={expanded} reveal={reveal} />
        </Box>

        <View style={styles.scrollWrap}>
          <ScrollView
            style={styles.scroll}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
            {...navScrollProps}
          >
            <VStack spacing="lg">
              {navSections.map((section) => (
                <Section
                  key={section.titleKey}
                  section={section}
                  expanded={expanded}
                  activeHref={activeHref}
                  reveal={reveal}
                  onNavigate={onNavigate}
                />
              ))}
            </VStack>
          </ScrollView>

          {navAtStart && <ScrollEdgeFade edge="top" color={colors.background.primary} size={28} />}
          {navAtEnd && <ScrollEdgeFade edge="bottom" color={colors.background.primary} size={28} />}
        </View>
      </VStack>

      <VStack spacing="xs" style={styles.footer}>
        <FooterAction
          icon={SettingsIcon}
          label={t('layout.sidebar.settings')}
          expanded={expanded}
          reveal={reveal}
          onPress={handleSettings}
          testID="nav_settings_link"
        />
        <FooterAction
          icon={LogoutIcon}
          label={t('layout.sidebar.logout')}
          expanded={expanded}
          reveal={reveal}
          onPress={handleLogout}
          danger
          testID="nav_logout_button"
        />
        <View style={styles.footerDivider} />
        <ProfileCard expanded={expanded} reveal={reveal} />
        <Pressable
          testID="nav_toggle_button"
          onPress={onToggle}
          accessibilityRole="button"
          accessibilityLabel={
            expanded ? t('layout.sidebar.collapseSidebar') : t('layout.sidebar.expandSidebar')
          }
          style={({ pressed }) => [styles.toggle, pressed && styles.togglePressed]}
        >
          <ToggleIcon color={colors.text.secondary} size={18} strokeWidth={1.8} />
        </Pressable>
      </VStack>
    </View>
  );
}

export const SidebarModern = memo(SidebarModernImpl);
SidebarModern.displayName = 'SidebarModern';

// Icon column width — keeps the icon anchored at the same x in both states so
// it never jumps; the collapsed rail width is sized so this reads as centered.
const ICON_COL = 24;

const styles = StyleSheet.create({
  sidebar: {
    flex: 1,
    backgroundColor: colors.background.primary,
    overflow: 'hidden',
  },
  top: {
    flex: 1,
    minHeight: 0,
  },
  scrollWrap: {
    flex: 1,
    minHeight: 0,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    paddingHorizontal: tokens.spacing.sm,
    paddingBottom: tokens.spacing.lg,
  },
  brandLogo: {
    width: 28,
    height: 28,
  },
  fadeLabel: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
  },
  sectionTitle: {
    paddingHorizontal: tokens.spacing.xs,
    paddingTop: tokens.spacing.xs,
    paddingBottom: tokens.spacing.xs,
    letterSpacing: 1.2,
  },
  navRow: {
    position: 'relative',
    flexDirection: 'row',
    alignItems: 'center',
    height: 42,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.sm,
    borderWidth: 1,
    borderColor: 'transparent',
  },
  brandWordmarkRow: {
    flexDirection: 'row',
  },
  brandWordmark: {
    fontFamily: darkPrecisionFontFamilies.displayBold,
    fontSize: 18,
    lineHeight: 23,
    fontWeight: '700',
    letterSpacing: -0.3,
    color: colors.text.primary,
  },
  brandWordmarkAccent: {
    color: colors.primary[400],
  },
  navIcon: {
    width: ICON_COL,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navRowHover: {
    backgroundColor: colors.interactive.hoverSubtle,
  },
  navRowActive: {
    backgroundColor: colors.primary.muted,
    borderColor: colors.primary.borderActive,
  },
  navLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
    marginLeft: tokens.spacing.sm,
  },
  navLabelText: {
    flex: 1,
  },
  footer: {
    paddingHorizontal: tokens.spacing.sm,
    paddingBottom: tokens.spacing.md,
    paddingTop: tokens.spacing.xs,
  },
  footerDivider: {
    height: 1,
    backgroundColor: colors.border.subtle,
    marginVertical: tokens.spacing.xs,
    marginHorizontal: tokens.spacing.sm,
  },
  profileRow: {
    flex: 1,
  },
  profileCard: {
    borderRadius: tokens.radius.lg,
    backgroundColor: colors.surface.row,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.sm,
  },
  toggle: {
    alignItems: 'center',
    justifyContent: 'center',
    height: 36,
    borderRadius: tokens.radius.md,
    marginTop: tokens.spacing.xs,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    backgroundColor: colors.surface.row,
  },
  togglePressed: {
    backgroundColor: colors.interactive.hover,
  },
  tooltipAnchor: {
    position: 'relative',
  },
  tooltip: {
    position: 'absolute',
    left: '100%',
    top: '50%',
    transform: [{ translateY: -14 }],
    marginLeft: tokens.spacing.sm,
    backgroundColor: colors.surface.row,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.subtle,
    zIndex: tokens.zIndex.tooltip,
    ...Platform.select({
      web: { boxShadow: '0 12px 26px rgba(0, 0, 0, 0.28)' } as unknown as ViewStyle,
      default: {},
    }),
  },
});
